'use client';

import { fetchProductList, fetchRecommendProducts } from '@/api/product';
import { listByProductIds } from '@/api/public';
import type { ListProduct } from '@/api/types/public';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  buildCapsuleAlgorithmRequestParams,
  normalizeCapsuleAlgorithmPage,
  type CapsuleAlgorithmResponseData,
} from './normalizeAlgorithmProducts';
import type { CapsuleRecommendProduct } from './types';

const CAPSULE_RECOMMEND_PAGE_SIZE = 20;

export interface CapsuleRecommendProductsState {
  hasMore: boolean;
  loading: boolean;
  loadMore: () => Promise<void>;
  products: CapsuleRecommendProduct[];
}

function useInitialLoadCompleteCallback(onInitialLoadComplete?: () => void) {
  const callbackRef = useRef(onInitialLoadComplete);
  const notifiedRef = useRef(false);

  useEffect(() => {
    callbackRef.current = onInitialLoadComplete;
  }, [onInitialLoadComplete]);

  return useCallback(() => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    callbackRef.current?.();
  }, []);
}

export function useCapsuleGroupRecommendProducts(
  groupId: number,
  onInitialLoadComplete?: () => void,
): CapsuleRecommendProductsState {
  const [products, setProducts] = useState<ListProduct[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const productsRef = useRef<ListProduct[]>([]);
  const loadingRef = useRef(false);
  const latestRequestIdRef = useRef(0);
  const previousTokenRef = useRef<string | undefined>(undefined);
  const hasObservedTokenRef = useRef(false);
  const pendingWishSyncTokenRef = useRef<string | null>(null);
  const token = useStore((state) => state.token);
  const latestTokenRef = useRef(token);
  latestTokenRef.current = token;
  const notifyInitialLoadComplete = useInitialLoadCompleteCallback(onInitialLoadComplete);

  const loadPage = useCallback(
    async (nextPage: number, append: boolean) => {
      if (loadingRef.current) return;

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      const isLatestRequest = () => latestRequestIdRef.current === requestId;
      const requestToken = latestTokenRef.current;

      loadingRef.current = true;
      setLoading(true);

      try {
        const response = await fetchProductList({
          group_id: groupId,
          page: nextPage,
          limit: CAPSULE_RECOMMEND_PAGE_SIZE,
          append_correlation: 1,
        });
        if (!isLatestRequest()) return;

        if (response.code === 200 && response.data) {
          const currentToken = latestTokenRef.current;
          let nextProducts = response.data.list || [];
          if (requestToken !== currentToken) {
            if (currentToken) {
              pendingWishSyncTokenRef.current = currentToken;
            } else {
              nextProducts = nextProducts.map((item) => ({ ...item, is_wish: 2 }));
            }
          }
          const mergedProducts = append ? productsRef.current.concat(nextProducts) : nextProducts;
          const total = Number(response.data.total || 0);

          productsRef.current = mergedProducts;
          setProducts(mergedProducts);
          setPage(nextPage);
          setHasMore(
            nextProducts.length >= CAPSULE_RECOMMEND_PAGE_SIZE &&
              (!total || mergedProducts.length < total),
          );
        } else {
          if (!append) {
            productsRef.current = [];
            setProducts([]);
            setPage(0);
          }
          setHasMore(false);
        }
      } catch (error) {
        if (isLatestRequest()) {
          logger.error(error, 'Failed to load capsule group recommend products:');
          if (!append) {
            productsRef.current = [];
            setProducts([]);
            setPage(0);
          }
          setHasMore(false);
        }
      } finally {
        if (isLatestRequest()) {
          loadingRef.current = false;
          setLoading(false);
          if (!append) notifyInitialLoadComplete();
        }
      }
    },
    [groupId, notifyInitialLoadComplete],
  );

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    await loadPage(page + 1, true);
  }, [hasMore, loadPage, page]);

  const updateProductsWishStatus = useCallback(async (expectedToken: string) => {
    const currentProducts = productsRef.current;
    if (currentProducts.length === 0) return;

    try {
      const productIds = currentProducts.map((item) => item.id || 0).filter((id) => id > 0);
      if (productIds.length === 0) return;

      const response = await listByProductIds({ product_ids: productIds.join(',') });
      if (response?.code !== 200 || !Array.isArray(response.data?.list)) return;
      if (previousTokenRef.current !== expectedToken) return;

      const wishMap = response.data.list.reduce<Record<number, number>>((result, item) => {
        if (item.id && typeof item.is_wish === 'number') result[item.id] = item.is_wish;
        return result;
      }, {});
      const nextProducts = productsRef.current.map((item) => ({
        ...item,
        is_wish: item.id && wishMap[item.id] !== undefined ? wishMap[item.id] : item.is_wish,
      }));

      productsRef.current = nextProducts;
      setProducts(nextProducts);
    } catch (error) {
      logger.error(error, 'Failed to update capsule group recommend wish status:');
    }
  }, []);

  useWhenTokenReady(() => {
    if (!hasObservedTokenRef.current) {
      hasObservedTokenRef.current = true;
      previousTokenRef.current = token;
      return;
    }

    const previousToken = previousTokenRef.current;
    pendingWishSyncTokenRef.current = null;
    if (previousToken && !token) {
      const nextProducts = productsRef.current.map((item) => ({ ...item, is_wish: 2 }));
      productsRef.current = nextProducts;
      setProducts(nextProducts);
    }

    previousTokenRef.current = token;
    if (token && previousToken !== token) {
      if (productsRef.current.length === 0) {
        pendingWishSyncTokenRef.current = token;
      } else {
        void updateProductsWishStatus(token);
      }
    }
  }, [token, updateProductsWishStatus]);

  useEffect(() => {
    const pendingToken = pendingWishSyncTokenRef.current;
    if (!pendingToken || products.length === 0) return;

    pendingWishSyncTokenRef.current = null;
    void updateProductsWishStatus(pendingToken);
  }, [products.length, updateProductsWishStatus]);

  useEffect(() => {
    void loadPage(1, false);
    return () => {
      latestRequestIdRef.current += 1;
      loadingRef.current = false;
    };
  }, [loadPage]);

  return { products, loading, hasMore, loadMore };
}

export function useCapsuleAlgorithmRecommendProducts(
  algorithmValue?: string,
  onInitialLoadComplete?: () => void,
  cartContextReady = false,
): CapsuleRecommendProductsState {
  const [products, setProducts] = useState<CapsuleRecommendProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const productsRef = useRef<CapsuleRecommendProduct[]>([]);
  const waterfallPositionRef = useRef(0);
  const loadingRef = useRef(false);
  const latestRequestIdRef = useRef(0);
  const canBuyList = useStore((state) => state.canBuyList);
  const cartDataReady = useStore((state) => state.cartDataReady);
  const cartRecommendSource = useStore((state) => state.publicResourceData?.cart_rec_source);
  const canBuyListRef = useRef(canBuyList);
  const notifyInitialLoadComplete = useInitialLoadCompleteCallback(onInitialLoadComplete);
  const isCartAlgorithm = algorithmValue === 'cart';
  const canInitializeRecommend = !isCartAlgorithm || cartDataReady || cartContextReady;
  const isWaterfallList = isCartAlgorithm
    ? cartRecommendSource === 1
    : env.NEXT_PUBLIC_SITE_NAME === 'us';

  useEffect(() => {
    canBuyListRef.current = canBuyList;
  }, [canBuyList]);

  const loadPage = useCallback(
    async (append: boolean) => {
      if (loadingRef.current) return;

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      const isLatestRequest = () => latestRequestIdRef.current === requestId;
      const currentPosition = append ? waterfallPositionRef.current : 0;

      loadingRef.current = true;
      setLoading(true);

      try {
        const response = await fetchRecommendProducts(
          buildCapsuleAlgorithmRequestParams(
            env.NEXT_PUBLIC_SITE_NAME,
            currentPosition,
            algorithmValue,
            cartRecommendSource,
          ),
        );
        if (!isLatestRequest()) return;

        if (response.code === 200 && response.data) {
          const hasGlassInCart = canBuyListRef.current.some((group) =>
            group.carts.some((cart) => Number(cart.product.is_glass) === 1),
          );
          const normalizedPage = normalizeCapsuleAlgorithmPage({
            currentPosition,
            data: response.data as CapsuleAlgorithmResponseData,
            hasGlassInCart,
            isWaterfallList,
            siteName: env.NEXT_PUBLIC_SITE_NAME,
          });
          const mergedProducts = append
            ? productsRef.current.concat(normalizedPage.products)
            : normalizedPage.products;

          productsRef.current = mergedProducts;
          waterfallPositionRef.current = normalizedPage.nextPosition;
          setProducts(mergedProducts);
          setHasMore(normalizedPage.hasMore);
        } else {
          if (!append) {
            productsRef.current = [];
            setProducts([]);
          }
          setHasMore(false);
        }
      } catch (error) {
        if (isLatestRequest()) {
          logger.error('Failed to load capsule algorithm recommend products:', error);
          if (!append) {
            productsRef.current = [];
            setProducts([]);
          }
          setHasMore(false);
        }
      } finally {
        if (isLatestRequest()) {
          loadingRef.current = false;
          setLoading(false);
          if (!append) notifyInitialLoadComplete();
        }
      }
    },
    [algorithmValue, cartRecommendSource, isWaterfallList, notifyInitialLoadComplete],
  );

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    await loadPage(true);
  }, [hasMore, loadPage]);

  useEffect(() => {
    if (!canInitializeRecommend) return;

    void loadPage(false);
    return () => {
      latestRequestIdRef.current += 1;
      loadingRef.current = false;
    };
  }, [canInitializeRecommend, loadPage]);

  return { products, loading, hasMore, loadMore };
}
